/**
 * テンプレート選択ダイアログ。
 * Phase 23a-ext-3: 組込テンプレート 5 種 + ユーザー自作テンプレートを並べて表示し、
 * 選択 → 新規キャンペーンを作成 → ボードへ遷移。
 */
import { FilePlus, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import { useCampaigns, useCreateCampaign } from '@/hooks/useCampaigns';

import {
  CAMPAIGN_TEMPLATES,
  templateToCampaignSeed,
  type CampaignTemplate,
} from './templates';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TemplatePickerDialog({ open, onOpenChange }: Props) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const createMutation = useCreateCampaign();
  const { data: allCampaigns } = useCampaigns();
  const [creating, setCreating] = useState(false);

  // ユーザー自作テンプレート（campaigns.isTemplate=true）
  const userTemplates = (allCampaigns ?? []).filter((c) => c.isTemplate);

  const createFromBuiltin = async (template: CampaignTemplate) => {
    setCreating(true);
    try {
      const seed = templateToCampaignSeed(template, {
        name: template.name,
        createdBy: user?.uid ?? '',
      });
      const id = await createMutation.mutateAsync(seed);
      toast.success(`「${template.name}」から作成しました`);
      onOpenChange(false);
      navigate(`/campaigns/${id}`);
    } catch (err) {
      toast.error(
        `作成失敗: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setCreating(false);
    }
  };

  const createFromUserTemplate = async (templateId: string) => {
    const tpl = userTemplates.find((c) => c.id === templateId);
    if (!tpl) return;
    setCreating(true);
    try {
      const id = await createMutation.mutateAsync({
        name: tpl.name + ' のコピー',
        description: tpl.description,
        status: 'planning',
        elements: tpl.elements,
        connections: tpl.connections,
        goal: tpl.goal,
        targetSegment: tpl.targetSegment,
        budget: tpl.budget,
        kpi: tpl.kpi,
        owner: tpl.owner,
        memo: tpl.memo,
        fromTemplateId: tpl.id,
        isTemplate: false,
        createdBy: user?.uid ?? '',
      });
      toast.success(`「${tpl.name}」から作成しました`);
      onOpenChange(false);
      navigate(`/campaigns/${id}`);
    } catch (err) {
      toast.error(
        `作成失敗: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setCreating(false);
    }
  };

  const createBlank = async () => {
    setCreating(true);
    try {
      const id = await createMutation.mutateAsync({
        name: '無題のキャンペーン',
        status: 'planning',
        elements: [],
        connections: [],
        createdBy: user?.uid ?? '',
      });
      toast.success('新規キャンペーンを作成しました');
      onOpenChange(false);
      navigate(`/campaigns/${id}`);
    } catch (err) {
      toast.error(
        `作成失敗: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!max-w-3xl">
        <DialogHeader>
          <DialogTitle>新規キャンペーン</DialogTitle>
          <DialogDescription>
            白紙から作るか、既存のテンプレートを起点に作成できます。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2 max-h-[70vh] overflow-y-auto">
          {/* 白紙 */}
          <div>
            <button
              type="button"
              onClick={createBlank}
              disabled={creating}
              className="w-full text-left rounded border border-dashed border-[var(--border)] hover:border-[var(--accent)] bg-[var(--bg-elevated)] p-4 transition-colors flex items-start gap-3 disabled:opacity-50"
            >
              <FilePlus className="size-5 text-[var(--text-muted)] mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-semibold">白紙から作成</div>
                <div className="text-xs text-[var(--text-muted)] mt-0.5">
                  カードを 1 枚ずつ配置して自由に設計
                </div>
              </div>
            </button>
          </div>

          {/* 組込テンプレート */}
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Sparkles className="size-3.5 text-[var(--accent-purple)]" />
              <span className="text-xs font-semibold text-[var(--text-secondary)]">
                テンプレート（おすすめのユースケース）
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {CAMPAIGN_TEMPLATES.map((tpl) => (
                <button
                  key={tpl.id}
                  type="button"
                  onClick={() => createFromBuiltin(tpl)}
                  disabled={creating}
                  className="text-left rounded border border-[var(--border)] hover:border-[var(--accent)] bg-[var(--bg-surface)] p-3 transition-colors disabled:opacity-50"
                >
                  <div className="flex items-start gap-2">
                    <div className="text-2xl flex-shrink-0">{tpl.emoji}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold">
                        {tpl.name}
                      </div>
                      <div className="text-[11px] text-[var(--text-secondary)] mt-1 leading-relaxed line-clamp-3">
                        {tpl.description}
                      </div>
                      <div className="text-[10px] text-[var(--text-muted)] mt-1.5 font-mono">
                        {tpl.elements.length} カード ・ {tpl.connections.length} 線
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* ユーザー自作テンプレート */}
          {userTemplates.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-[var(--text-secondary)] mb-2">
                自作テンプレート
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {userTemplates.map((tpl) => (
                  <button
                    key={tpl.id}
                    type="button"
                    onClick={() => createFromUserTemplate(tpl.id)}
                    disabled={creating}
                    className="text-left rounded border border-[var(--border)] hover:border-[var(--accent)] bg-[var(--bg-surface)] p-3 transition-colors disabled:opacity-50"
                  >
                    <div className="text-[13px] font-semibold">{tpl.name}</div>
                    {tpl.description && (
                      <div className="text-[11px] text-[var(--text-secondary)] mt-1 leading-relaxed line-clamp-2">
                        {tpl.description}
                      </div>
                    )}
                    <div className="text-[10px] text-[var(--text-muted)] mt-1.5 font-mono">
                      {(tpl.elements ?? []).length} カード ・{' '}
                      {(tpl.connections ?? []).length} 線
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end pt-2 border-t border-[var(--border)]">
          {creating ? (
            <Button type="button" variant="ghost" size="sm" disabled>
              <Spinner className="size-3 mr-1" />
              作成中…
            </Button>
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              キャンセル
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
